import * as React from 'react';

import { cn } from '@/lib/utils';

export interface SegmentedControlOption {
  label: React.ReactNode;
  value: string;
  id?: string;
}

export interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value: string;
  onValueChange: (value: string) => void;
  name: string;
  'aria-label'?: string;
  className?: string;
}

/**
 * Compact mutually-exclusive option strip (e.g. Light/Dark); a small-N alternative to Select or Tabs.
 *
 * @category Forms
 */
export function SegmentedControl({
  options,
  value,
  onValueChange,
  name,
  'aria-label': ariaLabel,
  className,
}: SegmentedControlProps) {
  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (index + direction + options.length) % options.length;
    onValueChange(options[nextIndex].value);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('inline-flex rounded-md', className)}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            id={option.id ?? `${name}-${option.value}`}
            name={name}
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onValueChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'inline-flex min-w-[68px] items-center justify-center border border-border px-4 py-1.5 text-sm font-medium transition-colors',
              'focus:outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-primary/25',
              '-ml-px first:ml-0 first:rounded-l-md last:rounded-r-md',
              selected
                ? 'z-10 border-primary bg-primary text-primary-foreground'
                : 'cursor-pointer bg-background text-foreground hover:bg-muted'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
